import React, { ReactNode } from "react";
import { cn } from "../../utils";

export interface PageHeaderBadge {
  label: string;
  icon?: React.ElementType;
  className?: string;
}

export interface PageHeaderMetadata {
  label: string;
  value: string;
  icon: React.ElementType;
}

export interface BasePageHeaderProps {
  title: string;
  badges?: PageHeaderBadge[];
  metadata?: PageHeaderMetadata[];
  description?: string;
  actions?: ReactNode;
  projectName?: string;
  className?: string;
}

export const BasePageHeader = React.memo(function BasePageHeader({
  title,
  badges,
  metadata,
  description,
  actions,
  projectName,
  className,
}: BasePageHeaderProps) {
  return (
    <div
      className={cn(
        "bg-white border border-gray-100 backdrop-blur-xl rounded-[20px] p-6 sm:p-8 mb-8 relative overflow-hidden group transition-all",
        className,
      )}
    >
      <div className="relative z-10">
        {/* Title Row: Title + Badges + Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            <h2 className="text-3xl sm:text-3xl font-semibold text-gray-900 tracking-tight leading-none">
              {title}
            </h2>

            {/* Badges */}
            {badges && badges.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {badges.map((badge, index) => (
                  <div
                    key={`${badge.label}-${index}`}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border-transparent",
                      badge.className ?? "bg-gray-50 text-gray-600",
                    )}
                  >
                    {badge.icon && <badge.icon className="w-4 h-4" />}
                    {badge.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions (pushed to right) */}
          {actions && (
            <div className="flex items-center gap-2 sm:ml-auto">{actions}</div>
          )}
        </div>

        {/* Project Name (for creation mode or secondary title) */}
        {projectName && (
          <p className="text-base sm:text-lg font-semibold text-gray-700 mb-3">
            {projectName}
          </p>
        )}

        {/* Metadata Row */}
        {metadata && metadata.length > 0 && (
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-400 font-medium">
            {metadata.map((item, index) => (
              <div
                key={`${item.label}-${index}`}
                className="flex items-center gap-2"
              >
                <span>
                  {item.label}{" "}
                  <span className="text-gray-600 font-semibold ml-0.5">
                    {item.value}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Description */}
        {description && (
          <p className="text-sm sm:text-base text-gray-600 mt-4 max-w-3xl leading-relaxed">
            {description}
          </p>
        )}
      </div>
    </div>
  );
});

BasePageHeader.displayName = "BasePageHeader";
